import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import consultationRestDayService from '../../../services/consultationRestDayService';
import ConsultationRestDaysViewModal from '../../doctor/ConsultationRestDaysViewModal';
import Icon from '../../common/Icons';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { doctorConsultationEditPath } from '../../doctor/doctorPaths';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import RestDayListItem from '../restDays/RestDayListItem';
import { computeRestDaySiteTotals } from '../restDays/restDayListDisplay';
import '../HeadDoctorDashboard.css';

const STATUS_FILTERS = [
  { id: '', labelKey: 'FILTER_STATUS_ALL' },
  { id: 'ISSUED', labelKey: 'FILTER_STATUS_ISSUED' },
  { id: 'CANCELLED', labelKey: 'FILTER_STATUS_CANCELLED' }
];

const EMPTY_SITE_TOTALS = {
  issued: 0,
  cancelled: 0,
  totalDays: 0,
  inProgress: 0
};

function buildListParams({ workSiteId, statusFilter, search, dateFrom, dateTo }) {
  const params = { site_id: workSiteId };
  if (statusFilter) params.status = statusFilter;
  if (search.trim()) params.search = search.trim();
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  return params;
}

export default function HeadDoctorRestDaysPage() {
  const copy = HEAD_DOCTOR_COPY.REST_DAYS;
  const navigate = useNavigate();
  const { workSiteId, workSites, portalPaths } = useDoctorWorkspace();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [siteTotals, setSiteTotals] = useState(EMPTY_SITE_TOTALS);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ISSUED');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });

  const activeSiteName = useMemo(() => {
    const site = (workSites || []).find((s) => String(s.id) === String(workSiteId));
    return site?.name || site?.label || null;
  }, [workSites, workSiteId]);

  const loadItems = useCallback(async () => {
    if (!workSiteId) {
      setItems([]);
      setSiteTotals(EMPTY_SITE_TOTALS);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const listParams = buildListParams({
        workSiteId,
        statusFilter,
        search,
        dateFrom,
        dateTo
      });

      const [listRes, siteRes] = await Promise.all([
        consultationRestDayService.list(listParams),
        consultationRestDayService.list({ site_id: workSiteId })
      ]);

      if (listRes.success) {
        setItems(Array.isArray(listRes.data) ? listRes.data : []);
      } else {
        setError(listRes.message || 'Chargement impossible');
        setItems([]);
      }

      if (siteRes.success) {
        setSiteTotals(computeRestDaySiteTotals(Array.isArray(siteRes.data) ? siteRes.data : []));
      } else {
        setSiteTotals(EMPTY_SITE_TOTALS);
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
      setItems([]);
      setSiteTotals(EMPTY_SITE_TOTALS);
    } finally {
      setLoading(false);
    }
  }, [workSiteId, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const listStats = useMemo(
    () => ({
      count: items.length
    }),
    [items]
  );

  const isFiltered =
    Boolean(searchInput.trim()) ||
    statusFilter !== 'ISSUED' ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const hasActiveFilters = isFiltered;

  const resetFilters = () => {
    setSearchInput('');
    setStatusFilter('ISSUED');
    setDateFrom('');
    setDateTo('');
  };

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 240,
        menuHeight: estimateActionMenuHeight(2)
      })
    );
    setActionMenuItem(item);
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const openCertificate = (item) => {
    closeActionMenu();
    setViewItem(item);
  };

  const openConsultation = (item) => {
    closeActionMenu();
    navigate(doctorConsultationEditPath(item.consultation_id, portalPaths));
  };

  const activateRow = (item) => {
    if (item.status === 'ISSUED') {
      openCertificate(item);
      return;
    }
    openConsultation(item);
  };

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-rest-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={copy.PAGE_TITLE}
        description={copy.PAGE_SUBTITLE}
      />

      <div className="hd-co-shell">
        {!workSiteId ? (
          <div className="hd-co-state hd-co-state--warn">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="exclamationTriangle" size={28} />
            </div>
            <h3 className="hd-co-state__title">Site requis</h3>
            <p className="hd-co-state__text mb-0">{copy.NO_SITE}</p>
          </div>
        ) : (
          <>
            <section className="hd-co-stats hd-co-stats--4" aria-label="Synthèse des certificats">
              <article
                className={`hd-co-stat-card${isFiltered ? ' hd-co-stat-card--queue' : ''}`}
              >
                <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
                <span className="hd-co-stat-card__value">{loading ? '—' : listStats.count}</span>
                <span className="hd-co-stat-card__hint">
                  {isFiltered
                    ? copy.STAT_SHOWN_HINT
                    : activeSiteName || copy.SITE_HINT}
                </span>
              </article>
              <article className="hd-co-stat-card hd-co-stat-card--success">
                <span className="hd-co-stat-card__label">{copy.STAT_ISSUED_SITE}</span>
                <span className="hd-co-stat-card__value">
                  {loading ? '—' : siteTotals.issued}
                </span>
                <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
              </article>
              <article className="hd-co-stat-card">
                <span className="hd-co-stat-card__label">{copy.STAT_TOTAL_DAYS}</span>
                <span className="hd-co-stat-card__value">
                  {loading ? '—' : siteTotals.totalDays}
                </span>
                <span className="hd-co-stat-card__hint">{copy.STAT_TOTAL_DAYS_HINT}</span>
              </article>
              <article className="hd-co-stat-card hd-co-stat-card--pending">
                <span className="hd-co-stat-card__label">{copy.STAT_IN_PROGRESS}</span>
                <span className="hd-co-stat-card__value">
                  {loading ? '—' : siteTotals.inProgress}
                </span>
                <span className="hd-co-stat-card__hint">{copy.STAT_IN_PROGRESS_HINT}</span>
              </article>
            </section>

            <div className="hd-co-toolbar">
              <div className="hd-co-search-wrap">
                <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
                <input
                  type="search"
                  className="hd-co-search-input"
                  placeholder={copy.SEARCH_PLACEHOLDER}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-label={copy.SEARCH_PLACEHOLDER}
                />
              </div>
              <div className="hd-co-toolbar-end">
                <div className="hd-co-filters" role="group" aria-label={copy.FILTER_STATUS}>
                  {STATUS_FILTERS.map((filter) => (
                    <button
                      key={filter.id || 'all'}
                      type="button"
                      className={`hd-co-filter-pill${
                        statusFilter === filter.id ? ' is-active' : ''
                      }`}
                      onClick={() => setStatusFilter(filter.id)}
                      aria-pressed={statusFilter === filter.id}
                    >
                      {copy[filter.labelKey]}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="hd-co-refresh-btn"
                  onClick={loadItems}
                  disabled={loading}
                  aria-label={copy.REFRESH_ARIA}
                  title={copy.REFRESH_ARIA}
                >
                  <Icon name="refresh" size={18} aria-hidden />
                </button>
                <div className="hd-co-toolbar-meta">
                  <strong>{loading ? '—' : listStats.count}</strong>
                  <span>{copy.RESULTS_LABEL}</span>
                </div>
              </div>
            </div>

            <div className="hd-co-toolbar-dates">
              <span className="hd-co-toolbar-dates__label">{copy.FILTER_PERIOD}</span>
              <label className="hd-co-date-field">
                <span>{copy.FILTER_DATE_FROM}</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label className="hd-co-date-field">
                <span>{copy.FILTER_DATE_TO}</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
              {hasActiveFilters ? (
                <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                  {copy.FILTER_RESET}
                </button>
              ) : null}
            </div>

            {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

            {loading ? (
              <div className="hd-co-state" role="status" aria-live="polite">
                <Spinner animation="border" className="hd-co-state__spinner" />
                <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="hd-co-state hd-co-state--empty">
                <div className="hd-co-state__icon" aria-hidden>
                  <Icon name="pause" size={32} />
                </div>
                <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
                <p className="hd-co-state__text">{copy.EMPTY}</p>
              </div>
            ) : (
              <ul className="hd-rest-list" aria-label={copy.LIST_ARIA}>
                {items.map((item) => (
                  <RestDayListItem
                    key={item.id}
                    item={item}
                    isViewing={viewItem?.id === item.id}
                    copy={copy}
                    onOpenMenu={(e) => openActionMenu(e, item)}
                    onRowActivate={activateRow}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openCertificate(actionMenuItem)}
            >
              <Icon name="download" size={15} />
              <span>{copy.ACTION_VIEW}</span>
            </button>
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openConsultation(actionMenuItem)}
            >
              <Icon name="edit" size={15} />
              <span>{copy.ACTION_OPEN}</span>
            </button>
          </div>
        </>
      ) : null}

      <ConsultationRestDaysViewModal
        show={Boolean(viewItem)}
        onHide={() => setViewItem(null)}
        consultation={
          viewItem
            ? {
                id: viewItem.consultation_id,
                patient_name: viewItem.patient_name,
                doctor_name: viewItem.doctor_name
              }
            : null
        }
        restDay={
          viewItem
            ? {
                start_date: viewItem.start_date,
                end_date: viewItem.end_date,
                days_count: viewItem.days_count,
                clinical_motif: viewItem.clinical_motif,
                recommendations: viewItem.recommendations
              }
            : null
        }
      />
    </div>
  );
}
